import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { debounce, differenceWith, findIndex, isEmpty, isEqual, isNil } from 'lodash-es';
import { SeButtonsToolbarService } from './se-buttons-toolbar.service';
import { RootState } from '../../root-store/root.state';
import {
    seToolbarButtonPressed,
    seToolbarButtonToggle,
    seToolbarSetLeftSlider,
    seToolbarSetRightSlider,
    seToolbarSetTimeline,
} from './redux/se-buttons-toolbar.actions';
import { ToolbarAction, ToolbarButtonData, ToolbarSliderOptions, ToolbarTimelineOptions } from './types';
import { SLIDER_OPTIONS, TIMELINE_OPTIONS } from './redux/se-buttons-toolbar.state';
import { ThirdPartSupportService } from '../../services/third-part-support.service';

@Component({
    selector: 'se-buttons-toolbar',
    templateUrl: './se-buttons-toolbar.component.html',
    styleUrls: ['./se-buttons-toolbar.component.scss'],
    standalone: false
})
export class SeButtonsToolbarComponent {
    @ViewChild('headerButtonsContainer')
    headerButtonsContainer!: ElementRef;

    @HostListener('window:resize')
    onResize() {
        this.refreshButtonsVisibility('resize');
    }

    private _buttonsData: ToolbarButtonData[] = [];
    @Input()
    set buttonsData(value: ToolbarButtonData[] | null) {

        this._buttonsData = value || [];

        // Se è una delle tre pagine che possono essere aperte da un terze parti,
        // rimuovo i pulsanti che non possono essere usati.
        if (this.thirdPartService.isThirdPartPage()) {
                this._buttonsData = this._buttonsData.filter(
                    btn => btn.actionType !== ToolbarAction.PATIENT
                );

                this.backVisible = false;      
        }

        this.refreshButtonsVisibility('inputData');
    }

    get buttonsData(): ToolbarButtonData[] {
        return this._buttonsData;
    }

    @Input()
    disableAll = false;

    @Input()
    backVisible = false;

    @Input()
    rightSliderOptions: ToolbarSliderOptions | null = null;

    @Input()
    leftSliderOptions: ToolbarSliderOptions | null = null;

    @Input()
    timelineOptions?: ToolbarTimelineOptions | null;

    private _background = undefined;
    @Input()
    set background(value) {
        if (this._background != value) {
            this._background = value;
        }
    }

    get background() {
        return this._background;
    }

    set rightSliderChecked(value: boolean) {
        const options = this.rightSliderOptions ?? SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED;
        this.store.dispatch(
            seToolbarSetRightSlider({
                status: { ...options, checked: value },
            }),
        );
    }

    get rightSliderChecked() {
        return Boolean(this.rightSliderOptions && this.rightSliderOptions.checked);
    }

    set leftSliderChecked(value: boolean) {
        const options = this.leftSliderOptions ?? SLIDER_OPTIONS.LEFT_SLIDER_DISABLED;
        this.store.dispatch(
            seToolbarSetLeftSlider({
                status: { ...options, checked: value },
            }),
        );
    }

    get leftSliderChecked() {
        return Boolean(this.leftSliderOptions && this.leftSliderOptions.checked);
    }

    set timelineVisible(value: boolean) {
        if (this.timelineOptions?.active === false) {
            return;
        }

        const options = this.timelineOptions ?? TIMELINE_OPTIONS.TIMELINE_DISABLED;
        this.store.dispatch(
            seToolbarSetTimeline({
                status: { ...options, open: value },
            }),
        );
    }

    get timelineVisible() {
        return Boolean(this.timelineOptions && this.timelineOptions.active && this.timelineOptions.open);
    }

    get getLeftTooltip(): string {
        if (this.leftSliderChecked) {
            return this.translate.instant('CloseLeftPanel');
        }

        return this.translate.instant('OpenLeftPanel');
    }

    get getRightTooltip(): string {
        if (this.rightSliderChecked) {
            return this.translate.instant('OpenRightPanel');
        }

        return this.translate.instant('CloseRightPanel');
    }

    buttonsList: Readonly<ToolbarButtonData>[] = [];
    hideButtonsList: Readonly<ToolbarButtonData>[] = [];

    buttonsContainerWidth: number = 0;

    prevLabel = this.translate.instant('PreviousPage');

    constructor(
        private store: Store<RootState>,
        private translate: TranslateService,
        private thirdPartService: ThirdPartSupportService
    ) {}


    isMoreButtonVisible(): boolean {
        return !isEmpty(this.hideButtonsList);
    }

    onToolbarClickEvent(button: ToolbarButtonData) {
        if (button.isToggle) {
            // Se è un toggle carico a mano il css
            const checked = SeButtonsToolbarService.toggleToolbarButton(
                button.actionType,
                this.buttonsList,
                this.hideButtonsList,
            );
            this.store.dispatch(seToolbarButtonToggle({ button: button.actionType, checked: Boolean(checked) }));
        } else {
            this.store.dispatch(
                seToolbarButtonPressed({
                    button: button.actionType,
                    payload: button.payload,
                    idCardioWebServiceForExtUrlToken: button.idCardioWebServiceForExtUrlToken,
                }),
            );
        }
    }

    onLeftSliderChange(e: MatSlideToggleChange) {
        this.leftSliderChecked = e.checked;
    }

    _onTimelineToggle(event: boolean) {
        if (this.timelineOptions?.active === false) {
            return;
        }
        this.timelineVisible = event;
    }

    onBackClick() {
        this.store.dispatch(seToolbarButtonPressed({ button: ToolbarAction.BACK }));
    }

    getButtonAction(_: number, button: ToolbarButtonData) {
        return button.actionType;
    }

    getVisibilityToolbar(button: ToolbarButtonData): boolean {
        if (!isNil(button.status.visibleIntoToolbar) && !button.hasChildren) {
            if (button.status.visible) {
                return Boolean(button.status.visibleIntoToolbar);
            }
        } else {
            return button.status.visible && !button.hasChildren;
        }

        return false;
    }

    getVisibilityChildrenToolbar(button: ToolbarButtonData): boolean {
        if (!isNil(button.status.visibleIntoToolbar) && button.hasChildren) {
            if (button.status.visible) {
                return button.status.visibleIntoToolbar;
            }
        } else {
            return button.status.visible && Boolean(button.hasChildren);
        }

        return false;
    }

    private refreshButtonsVisibility = debounce((from: 'resize' | 'inputData') => {
     
        // Copio la collection passata come parametro nella collection interna
        this.buttonsList = this.buttonsData.slice();
        this.setupInitialButtonsVisibility();
    }, 100);

   

    // Imposta i pulsanti visibili e quelli non visibili al caricamento
    private setupInitialButtonsVisibility = debounce(() => {
        let temphideButtonsList = [];

        //Alla larghezza del container dei pulsanti devo aggiungere la larghezza del pulsante timeline, che essendo esterno al container
        //non verrebbe incluso nel calcolo della lunghezza complessiva della toolbar

        let timelineBtnWidth = document.getElementById('timeline-btn')?.offsetWidth; //uso OffsetWidth perchè include anche i margini e i bordi

        let buttonsContainerWidth = timelineBtnWidth ? this.headerButtonsContainer.nativeElement.offsetWidth + timelineBtnWidth : this.headerButtonsContainer.nativeElement.offsetWidth;

        const domButtonlist = this.headerButtonsContainer.nativeElement.querySelectorAll('[main-toolbar]') as HTMLElement[];
        let totalWidth = 0;

        if (isEmpty(domButtonlist)) {
            return;
        }

        for (let i = 0; i < domButtonlist.length; i++) {
            const domElement = domButtonlist[i];
            totalWidth += domElement.offsetWidth;
            if (totalWidth > buttonsContainerWidth) {
                let length = domButtonlist.length; // non uso this.buttonsList perchè quella include anche quelli non visibili

                for (let n = i; n < length; n++) {
                    // A 'i' ho superato la lunghezza quindi parto a nascondere dall' 'i-1 esimo'
                    // Estraggo il pulsante dal DOM (mi serve per l'id)
                    const domBtn = domButtonlist[n];

                    //lavoro sulla collection in binding e rimuovo l'item che corrisponde all'id selezionato
                    const btn = this.buttonsList.splice(
                        findIndex(this.buttonsList, (item) => item.actionType === domBtn.getAttribute('action-type')),
                        1,
                    );
                    temphideButtonsList.push(btn[0]);
                }
                break;
            }

        }

        this.hideButtonsList = [...temphideButtonsList];


    },50);
}
